import { Link, ShieldCheck, ShieldClose, Star, TriangleAlert } from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';

import { RelevanceIndicator } from './RelevanceIndicator';
import { useSetKeyForFacts } from '../../../api/mutations/useSetKeyForFacts';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import Chip from '@/components/atoms/chip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/Popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/atoms/Tooltip';
import TooltipIcon from '@/components/atoms/TooltipIcon';
import DateInputPopup from '@/components/molecules/DateInputPopup';
import EditableField from '@/components/molecules/EditableField';
import { ThemesTooltip } from '@/components/organisms/ThemesTooltip';
import { useDocumentNavigation } from '@/hooks/useOpenDocViewer';
import { useOpenInconsistencyViewer } from '@/hooks/useOpenInconsistencyViewer';
import { ChronologyFact, Fact, Relevance } from '@/types';

interface FactTitleProps {
  open: boolean;
  fact: Fact | ChronologyFact;
  isKey?: boolean;
  factText: string;
  dateText: string;
  relevance: Relevance;
  compressedVersion?: boolean;
  updateFactDateText: (newDateText: string) => void;
  updateFactDescription: (newDescription: string) => void;
  isUpdatingDateText: boolean;
  isUpdatingDescription: boolean;
  reference?: number;
  hideRelevanceIndicator?: boolean;
  onViewDocument?: (docId: string, factId: string, pageNumber: number) => void;
  isLabellerOrg?: boolean;
  verification?: {
    verified: boolean;
    handleToggleVerify: (verify: boolean) => void;
    email?: string;
    date?: Date;
  };
}

const FactTitle = ({
  open,
  fact,
  isKey,
  factText,
  dateText,
  relevance,
  reference,
  hideRelevanceIndicator,
  onViewDocument,
  compressedVersion = false,
  updateFactDateText,
  updateFactDescription,
  isUpdatingDateText,
  isUpdatingDescription,
  isLabellerOrg = false,
  verification,
}: FactTitleProps) => {
  const { openDocViewer } = useDocumentNavigation();
  const { openInconsistencyViewer } = useOpenInconsistencyViewer();

  const isFullFact = 'dateIsAmbiguous' in fact;

  const { mutate: updateKey, isLoading } = useSetKeyForFacts(isFullFact ? fact.caseId : '');

  const handleCopyFactId = (e: React.MouseEvent) => {
    e.stopPropagation();
    const factId = isFullFact ? fact.id : fact.fact.id;
    navigator.clipboard.writeText(factId);
    toast.success('Fact ID copied to clipboard');
    trackCustomEvent(TrackingEvents.CopyFactId);
  };

  return (
    <div className="flex w-full items-center justify-between font-normal">
      <div className="w-full p-4 py-2.5 pr-2 pb-2.75">
        <div className="flex w-full justify-between">
          <div className={`flex items-center gap-1.75 font-semibold ${compressedVersion ? 'text-xs' : 'text-sm'}`}>
            {isFullFact && fact.dateIsAmbiguous && (
              <TooltipIcon
                triggerClassName="-mt-0.25"
                tooltipContent={fact.dateIsAmbiguousText || 'Date format is ambiguous, please check'}
                warning
              />
            )}

            {dateText ? dateText : '(Date Unknown)'}

            <DateInputPopup dateText={dateText} onUpdateDate={updateFactDateText} isLoading={isUpdatingDateText} />
          </div>

          <div className="flex items-center gap-2">
            <div className="flex gap-1 text-xs">
              {(isFullFact ? fact : fact.fact).locations.map((location, index) => {
                if (location.doc === undefined || location.doc === null) return <></>;
                if (compressedVersion && index === 2) return <div key={`${index}-ellipsis`}>...</div>;
                if (compressedVersion && index > 2) return <div key={`${index}-ellipsis`}></div>;

                const doc = location.doc;
                return (
                  <span key={`${doc.id}-${index}`} className="flex items-center justify-between gap-1">
                    <span
                      className="text-blue-600"
                      onClick={(e) => {
                        e.stopPropagation();

                        trackCustomEvent(TrackingEvents.OpenDocViewerFromFact);

                        (onViewDocument ?? openDocViewer)(
                          location.docId,
                          isFullFact ? fact.id : fact.fact.id,
                          location.boundingBoxes?.[0]?.pageNumber ?? 0,
                        );
                      }}
                    >
                      <TooltipProvider>
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger asChild>
                            <p className="line-clamp-1 max-w-28 break-all text-ellipsis">{doc.file.name}</p>
                          </TooltipTrigger>

                          <TooltipContent className="max-w-56 bg-gray-50">
                            <p className="text-xs text-gray-800">{doc.file.name}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </span>
                    {doc.type && <Chip className="bg-brandTertiary/10 text-brandTertiary">{doc.type}</Chip>}
                    {(isFullFact ? fact : fact.fact).locations.length > 1 &&
                    index < (isFullFact ? fact : fact.fact).locations.length - 1 ? (
                      <span className="text-gray-400">/</span>
                    ) : (
                      <></>
                    )}
                  </span>
                );
              })}
            </div>

            {isFullFact && fact.inconsistencies && fact.inconsistencies.length ? (
              <Chip
                className="bg-red-50 text-red-500 duration-150 hover:bg-red-100"
                onClick={() => {
                  trackCustomEvent(TrackingEvents.OpenInconsistencyViewer);
                  fact.inconsistencies &&
                    openInconsistencyViewer(fact.inconsistencies.map((inconsistency) => inconsistency.inconsistencyId));
                }}
              >
                <TriangleAlert size={12} />
                <span>Disputed</span>
              </Chip>
            ) : (
              <></>
            )}

            {isFullFact && fact.issues && fact.issues.length > 0 && <ThemesTooltip issues={fact.issues} />}

            {isFullFact && isLabellerOrg && (
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <button onClick={handleCopyFactId} className="group cursor-pointer" aria-label="Copy fact ID">
                      <Link size={15} className="text-gray-400 transition-all duration-150 group-hover:text-blue-500" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-gray-50">
                    <p className="text-xs text-gray-800">Copy fact ID</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {isFullFact && (
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <button
                      disabled={isLoading}
                      onClick={(e) => {
                        e.stopPropagation();
                        updateKey({ key: !isKey, factIds: [fact.id] });
                      }}
                      className="group cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label={isKey ? 'Remove key fact' : 'Mark as key fact'}
                    >
                      <Star
                        size={15}
                        className={`${
                          isKey ? 'fill-yellow-400 text-yellow-400' : 'fill-transparent text-gray-300'
                        } transition-all duration-150 group-hover:fill-yellow-200 group-hover:text-yellow-400`}
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-gray-50">
                    <p className="text-xs text-gray-800">{isKey ? 'Remove key fact' : 'Mark as key fact'}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>

        <div className="flex items-start justify-between gap-2">
          <div
            className={`line-clamp-1 w-5/6 pt-1 text-ellipsis text-gray-900 ${compressedVersion ? 'text-xs' : 'text-sm'}`}
          >
            {open ? (
              <EditableField value={factText} onSave={updateFactDescription} isLoading={isUpdatingDescription} />
            ) : (
              factText
            )}
          </div>

          <div className="mt-1.5 -mb-0.75 flex items-center gap-2">
            {compressedVersion && !hideRelevanceIndicator && (
              <div>
                <RelevanceIndicator relevance={relevance} />
              </div>
            )}

            {/* Show verify button for chronology facts */}
            {!isFullFact &&
              verification &&
              (verification.verified ? (
                <Popover>
                  <PopoverTrigger
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-0.75 rounded border border-green-500 bg-green-100 px-1 py-0.5 text-xs text-green-900 duration-150 hover:border-green-600"
                  >
                    <ShieldCheck size={14} className="-ml-0.25 text-green-600" />
                    <span className="-mb-[1px]">Verified</span>
                  </PopoverTrigger>

                  <PopoverContent side="left" className="flex w-auto items-center gap-2 p-3 text-xs">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        verification.handleToggleVerify(false);
                      }}
                      className="inline-flex items-center gap-0.75 rounded border border-red-500 bg-red-50 px-1 py-0.5 text-xs text-red-700 duration-150 hover:border-red-600"
                    >
                      <ShieldClose size={14} className="-ml-0.25 text-red-600" />
                      <span className="-mb-[1px]">Unverify</span>
                    </button>
                    <span className="text-gray-600">
                      Verified by <strong>{verification.email}</strong> on{' '}
                      {moment(verification.date).format('MMM DD, YYYY')}.
                    </span>
                  </PopoverContent>
                </Popover>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    trackCustomEvent(TrackingEvents.VerifyChronologyFact, {
                      factId: fact.id,
                      chronologyId: fact.chronologyId,
                    });
                    verification.handleToggleVerify(true);
                  }}
                  className="inline-flex items-center gap-0.75 rounded border border-gray-800 bg-gray-700 px-1.5 py-0.75 text-xs text-white duration-150 hover:border-gray-400 hover:bg-gray-600"
                >
                  <span className="-mb-[1px]">Verify</span>
                </button>
              ))}
          </div>

          {reference && (
            <p
              className={`absolute top-0 right-0 flex h-5 w-5 items-center justify-center rounded-bl-md border-b border-l bg-gray-50 text-xs text-blue-600`}
            >
              {reference}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FactTitle;
